/**
 * Detección de navegación SPA, AGNÓSTICA al framework.
 *
 * Todos los routers de cliente (Next, React Router, Astro, Vue, el que venga)
 * acaban pasando por uno de estos cuatro primitivos del navegador:
 *   - history.pushState   (navegar a una ruta nueva)
 *   - history.replaceState(reemplazar la ruta actual)
 *   - evento popstate     (atrás/adelante del navegador)
 *   - evento hashchange   (routers basados en #hash)
 * Si nos enganchamos AHÍ, los detectamos a todos sin conocer el framework.
 *
 * Principios (este código corre en webs de miles de publishers):
 *   - ADITIVO: el patch llama SIEMPRE al pushState/replaceState original y
 *     devuelve su valor → el router del publisher sigue funcionando igual.
 *   - IDEMPOTENTE: se instala una sola vez aunque el SDK se cargue dos veces
 *     (flag en el propio objeto history, compartido por todos los bundles).
 *   - NUNCA ROMPE: cualquier excepción del callback se traga; jamás propagamos
 *     un error a la página del publisher.
 *   - SIN FALSOS DISPAROS: solo notificamos cuando el `href` cambia de verdad
 *     (un replaceState a la misma URL no dispara).
 *
 * Esta capa NO decide nada: solo avisa "la ruta cambió a X". Qué hacer con eso
 * (actualizar señales, re-evaluar reglas, etc.) es de quien registra el callback.
 */

/** Entorno inyectable: en producción son los globals reales; en tests, falsos. */
export type RouteEnv = {
  history: History;
  location: Pick<Location, "href">;
  addEventListener: (type: string, cb: (e?: unknown) => void) => void;
};

const PATCH_FLAG = "__twRouteWatcher";

/**
 * Instala el detector. Devuelve true si se instaló, false si ya estaba puesto
 * (idempotencia). `onChange` recibe el nuevo `href` en cada cambio real de ruta.
 */
export function installRouteWatcher(
  onChange: (href: string) => void,
  env: RouteEnv
): boolean {
  const hist = env.history as History & Record<string, unknown>;

  // Guardia de idempotencia en el objeto history (compartido entre bundles): si
  // ya hay un watcher, no volvemos a parchear ni a añadir listeners duplicados.
  if (hist[PATCH_FLAG]) return false;
  hist[PATCH_FLAG] = true;

  let last = env.location.href;

  function check() {
    // Lectura síncrona y barata; pushState ya mutó location cuando llegamos aquí.
    const href = env.location.href;
    if (href === last) return; // misma URL → no es un cambio real
    last = href;
    try {
      onChange(href);
    } catch {
      /* el callback nunca puede tumbar la página del publisher */
    }
  }

  // Parche aditivo de pushState / replaceState. Envuelto en try/catch: si el
  // publisher tiene un history.pushState NO escribible (plugins anti-tamper,
  // Object.freeze, accessor sin setter), NO lanzamos → degradamos a detección por
  // popstate/hashchange. Honra la promesa "NUNCA ROMPE la página del publisher".
  for (const method of ["pushState", "replaceState"] as const) {
    try {
      const original = env.history[method];
      if (typeof original !== "function") continue;
      const orig = original as (...a: unknown[]) => unknown;
      const wrapper = function (this: History, ...args: unknown[]) {
        const ret = orig.apply(this, args); // 1º el original: semántica intacta
        check(); // 2º detectamos el cambio (si lo hubo)
        return ret;
      } as History[typeof method];
      // Que name/length coincidan con el nativo: reduce la huella del monkey-patch
      // (hay libs que hacen feature-detection sobre estos atributos).
      try {
        Object.defineProperty(wrapper, "name", { value: method, configurable: true });
        Object.defineProperty(wrapper, "length", {
          value: (orig as { length: number }).length,
          configurable: true,
        });
      } catch {
        /* no crítico */
      }
      env.history[method] = wrapper;
    } catch {
      /* history[method] no escribible → seguimos sin parchearlo (detección degradada) */
    }
  }

  // Atrás/adelante y routers de hash.
  try {
    env.addEventListener("popstate", check);
    env.addEventListener("hashchange", check);
  } catch {
    /* entorno sin addEventListener utilizable: degradación silenciosa */
  }

  return true;
}
